/**
 * A bar floating over its parent (lib/dock.ts): measures the parent and the bar, places the bar
 * against its docked edge, moves it with its grip (drag: the bar follows the finger, held where it
 * was grabbed, and settles on the allowed edge nearest the finger when it is let go; arrow keys:
 * along the edge / across / round a corner) and remembers where it was left (per device,
 * `storageKey`). The first placement does not glide in from the corner; later moves do. Reports how much of each side of the parent it covers (`insets`), so
 * banners and chips can keep clear of it. Used by the edit toolbar and the strip of open songs.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { ALL_EDGES, dockByKey, dockPlacement, DOCK_MARGIN, followFinger, isVertical, loadDock, releaseDock, saveDock, type Dock, type DockEdge, type Placement } from './dock';

/** How much of each side of the parent a bar covers (0 for the sides it is not docked to). */
export interface DockInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export const NO_INSETS: DockInsets = { top: 0, bottom: 0, left: 0, right: 0 };

/** Pointer travel before a press on the grip becomes a drag. */
const DRAG_SLOP_PX = 6;

export interface FloatingDockOptions {
  storageKey: string;
  fallback: Dock;
  edges?: readonly DockEdge[];
  /** Largest width the bar may take for a parent `w` wide on `edge` (default: all of it but the margins). */
  maxWidth?: (w: number, edge: DockEdge) => number;
}

export interface FloatingDock<T extends HTMLElement> {
  /** The bar's element (a callback ref: the bar may mount after its parent). */
  ref: (el: T | null) => void;
  dock: Dock;
  vertical: boolean;
  dragging: boolean;
  /** Position, size limits and transition of the bar (absolute, top-left of the parent). */
  style: CSSProperties;
  insets: DockInsets;
  /**
   * True once after a press on the grip that turned into a drag (then reset): a bar whose grip is
   * also its button (the reading-mode button) ignores the click that ends a drag.
   */
  consumeDrag: () => boolean;
  /** Spread on the grip <button>. */
  gripProps: {
    onPointerDown: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerMove: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerUp: (e: PointerEvent<HTMLButtonElement>) => void;
    onPointerCancel: (e: PointerEvent<HTMLButtonElement>) => void;
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  };
}

export function useFloatingDock<T extends HTMLElement>({ storageKey, fallback, edges = ALL_EDGES, maxWidth }: FloatingDockOptions): FloatingDock<T> {
  const [dock, setDock] = useState<Dock>(() => loadDock(storageKey, fallback, edges));
  const dockRef = useRef(dock);
  dockRef.current = dock;

  /* ── measuring: the parent and the bar itself ── */
  const [el, setEl] = useState<T | null>(null);
  const elRef = useRef<T | null>(null);
  const ref = useCallback((node: T | null) => {
    elRef.current = node;
    setEl(node);
  }, []);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const parent = el?.parentElement;
    if (!el || !parent) return;
    const measure = () => {
      const b = { w: parent.clientWidth, h: parent.clientHeight };
      const s = { w: el.offsetWidth, h: el.offsetHeight };
      setBox((o) => (o && o.w === b.w && o.h === b.h ? o : b));
      setSize((o) => (o && o.w === s.w && o.h === s.h ? o : s));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(parent);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  // Nothing while the bar is not mounted (it may hide and show again): no stale insets.
  const at = el && box && size ? dockPlacement(dock, box, size) : null;
  // Moves glide, but the first placement does not (it would slide in from the corner).
  const [placed, setPlaced] = useState(false);
  useEffect(() => {
    if (!at || placed) return;
    const id = requestAnimationFrame(() => setPlaced(true));
    return () => cancelAnimationFrame(id);
  }, [at, placed]);

  const covers = (edge: DockEdge) => !!at && !!box && !!size && dock.edge === edge;
  const insets: DockInsets = {
    top: covers('top') ? Math.round(at!.top + size!.h) : 0,
    bottom: covers('bottom') ? Math.round(box!.h - at!.top) : 0,
    left: covers('left') ? Math.round(at!.left + size!.w) : 0,
    right: covers('right') ? Math.round(box!.w - at!.left) : 0,
  };

  /* ── moving: drag the grip (the bar follows the finger), or its arrow keys ── */
  const drag = useRef<{ id: number; x0: number; y0: number; grabX: number; grabY: number; moving: boolean } | null>(null);
  const dragged = useRef(false);
  /** Where the bar is while it is being dragged (null: docked). */
  const [free, setFree] = useState<Placement | null>(null);
  const freeRef = useRef<Placement | null>(null);
  const dragging = free !== null;
  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const bar = elRef.current?.getBoundingClientRect();
    if (!bar) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, grabX: e.clientX - bar.left, grabY: e.clientY - bar.top, moving: false };
  };
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    const parent = elRef.current?.parentElement;
    if (!d || d.id !== e.pointerId || !parent || !size) return;
    if (!d.moving && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < DRAG_SLOP_PX) return;
    d.moving = true;
    const r = parent.getBoundingClientRect();
    const next = followFinger({ x: e.clientX - r.left, y: e.clientY - r.top }, { x: d.grabX, y: d.grabY }, { w: r.width, h: r.height }, size);
    freeRef.current = next;
    setFree(next);
  };
  const onPointerUp = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    dragged.current = d.moving;
    const parent = elRef.current?.parentElement;
    const letGo = freeRef.current;
    freeRef.current = null;
    setFree(null);
    if (!d.moving || !letGo || !parent || !size) return;
    const r = parent.getBoundingClientRect();
    const next = releaseDock(dockRef.current, { x: e.clientX - r.left, y: e.clientY - r.top }, { ...letGo, ...size }, { w: r.width, h: r.height }, edges);
    setDock(next);
    saveDock(next, storageKey);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const next = dockByKey(dockRef.current, e.key, edges);
    if (!next) return;
    e.preventDefault(); // never also a page turn (viewer/usePageKeys)
    setDock(next);
    saveDock(next, storageKey);
  };

  const widthLimit = box ? (maxWidth ? maxWidth(box.w, dock.edge) : box.w - 2 * DOCK_MARGIN) : undefined;
  return {
    ref,
    dock,
    vertical: isVertical(dock.edge),
    dragging,
    insets,
    style: {
      transform: free ? `translate(${free.left}px, ${free.top}px)` : at ? `translate(${at.left}px, ${at.top}px)` : undefined,
      visibility: at ? 'visible' : 'hidden',
      maxWidth: widthLimit,
      maxHeight: box ? box.h - 2 * DOCK_MARGIN : undefined,
      transition: placed && !dragging ? 'transform 160ms ease-out' : 'none',
    },
    consumeDrag: () => {
      const was = dragged.current;
      dragged.current = false;
      return was;
    },
    gripProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onKeyDown },
  };
}
